import React, { useState, useRef, useEffect } from "react"; // <--- Tambah useEffect
import { FaUserCircle } from "react-icons/fa";
import { FaFloppyDisk, FaCamera } from "react-icons/fa6";
import { FiLoader, FiCheck, FiTrash2 } from "react-icons/fi";
import { formatSize } from "../../../../../utilities/image_configuration/formatSize";
import { ProfileService } from "../../../../../services/ProfileService";
import { BaseUrl } from "../../../../../utilities/BaseUrl";

const inputClass =
    "w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all disabled:bg-slate-900/40 disabled:text-slate-500 disabled:cursor-not-allowed";

export const TabProfileSetting = () => {
    // State untuk Data User
    const [userData, setUserData] = useState({
        name: "",
        phone_number: "",
        email: "",
        image: null,
    });

    const [loadingSubmit, setLoadingSubmit] = useState(false);

    // State Loading khusus untuk saat GET data (pertama kali buka)
    const [fetchingData, setFetchingData] = useState(true);

    const [profilePreview, setProfilePreview] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);
    const [compressInfo, setCompressInfo] = useState(null);

    const dropZoneRef = useRef(null);
    const fileInputRef = useRef(null);

    // --- useEffect: Ambil Data User Saat Component Mount ---
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await ProfileService.get();

                // Sesuaikan path data berdasarkan respon backend
                // Asumsi struktur response: { data: { name: '...', phone_number: '...', ... } }
                const user = response.data?.data;

                if (user) {
                    setUserData({
                        name: user.name || "",
                        phone_number: user.phone_number || "",
                        email: user.email || "",
                        image: user.image || "",
                    });

                    // Set preview jika user punya foto
                    if (user.image) {
                        setProfilePreview(user.image);
                    }
                }
            } catch (error) {
                console.error("Gagal mengambil data profil:", error);
            } finally {
                setFetchingData(false);
            }
        };

        fetchProfile();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoadingSubmit(true);

        try {
            const formData = new FormData();
            formData.append('name', userData.name);
            formData.append('phone_number', userData.phone_number);
            formData.append('email', userData.email);

            // Hanya kirim image jika user mengganti image baru (bukan null)
            if (userData.image) {
                formData.append('image', userData.image);
            }

            await ProfileService.update({ data: formData });

            alert("Profil berhasil diperbarui!");

            // Optional: Refresh data lagi untuk memastikan tampilan sinkron
            // const response = await ProfileService.get();
            // setUserData({ ...response.data, image: null });
            // setProfilePreview(response.data.image);

        } catch (err) {
            console.error(err);
            alert("Gagal memperbarui profil. Mohon cek koneksi atau data input.");
        } finally {
            setLoadingSubmit(false);
        }
    };

    const handleUserChange = (e) => {
        setUserData((prev) => ({
            ...prev,
            [e.target.id]: e.target.value,
        }));
    };

    // ... (Handler Drag & Drop dan File Input tetap sama seperti sebelumnya) ...
    const handleDragEnter = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    };
    const handleDragOver = (e) => {
        e.preventDefault(); e.stopPropagation();
    };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]);
    };
    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files[0]) processFile(e.target.files[0]);
    };
    const handleRemoveImage = () => {
        setProfilePreview(null);
        setUserData((prev) => ({ ...prev, image: null }));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const processFile = (file) => {
        if (!file.type.startsWith("image/")) {
            alert("Hanya file gambar yang diperbolehkan.");
            return;
        }
        setIsCompressing(true);
        const reader = new FileReader();
        reader.onloadend = () => {
            setProfilePreview(reader.result);
            setTimeout(() => {
                setIsCompressing(false);
                setCompressInfo({
                    originalSize: file.size,
                    compressedSize: file.size * 0.7,
                    percent: 30,
                });
                setUserData((prev) => ({ ...prev, image: file }));
            }, 1500);
        };
        reader.readAsDataURL(file);
    };

    // Tampilkan loading jika sedang fetch data awal
    if (fetchingData) {
        return (
            <div className="flex items-center justify-center h-64">
                <FiLoader className="animate-spin text-cyan-400 text-3xl" />
            </div>
        );
    }

    return (
        <>
            <section className="space-y-6 animate-in fade-in duration-300">
                <header>
                    <h3 className="text-lg font-bold text-gray-100">Profil Pengguna</h3>
                    <p className="text-xs text-slate-500 mt-1">
                        Kelola informasi pribadi Anda untuk tampilan akun.
                    </p>
                </header>

                {/* BAGIAN FOTO PROFIL */}
                <div className="flex flex-col items-center gap-4 pb-8 border-b border-slate-700/40">
                    <div className="relative group">
                        <div
                            ref={dropZoneRef}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => !isCompressing && fileInputRef.current.click()}
                            className={`
                                relative w-32 h-32 rounded-full border-2 border-dashed flex flex-col items-center justify-center 
                                cursor-pointer overflow-hidden transition-all duration-300 bg-slate-800/30
                                ${isDragging
                                    ? "border-cyan-500 bg-cyan-500/10 scale-105"
                                    : profilePreview
                                        ? "border-slate-600/50"
                                        : "border-slate-600/50 hover:border-cyan-500/50 hover:bg-slate-800/50"
                                }
                            `}
                        >
                            {isCompressing && (
                                <div className="absolute inset-0 z-20 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center rounded-full">
                                    <FiLoader className="animate-spin text-cyan-400 text-2xl mb-2" />
                                    <span className="text-[10px] font-bold text-cyan-400">Mengompres...</span>
                                </div>
                            )}

<img
                                src={BaseUrl + "/storage/" + userData.image}
                                alt="Profile Preview"
                                className="w-full h-full object-cover z-10"
                            />

                            {profilePreview ? (
                                <img
                                    src={profilePreview}
                                    alt="Profile Preview"
                                    className="w-full h-full object-cover z-10"
                                />
                            ) : (
                                <div className="flex flex-col items-center text-slate-500 group-hover:text-cyan-400 transition-colors z-10">
                                    <FaUserCircle className="text-4xl mb-1" />
                                    <span className="text-[9px] font-semibold uppercase tracking-wider text-center px-2">
                                        Upload Foto
                                    </span>
                                </div>
                            )}

                            {profilePreview && !isCompressing && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 rounded-full">
                                    <FaCamera className="text-white text-xl" />
                                </div>
                            )}

                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/png, image/jpeg, image/jpg"
                                onChange={handleFileInputChange}
                            />
                        </div>
                    </div>

                    <div className="w-full max-w-xs flex flex-col items-center gap-3">
                        {compressInfo && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                                <FiCheck size={14} />
                                <div className="flex flex-col text-center leading-tight">
                                    <span className="text-[10px] font-bold">Hemat {compressInfo.percent}%</span>
                                    <span className="text-[9px] opacity-80">
                                        {formatSize(compressInfo.originalSize)} → {formatSize(compressInfo.compressedSize)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {profilePreview && (
                            <button
                                type="button"
                                onClick={handleRemoveImage}
                                className="text-xs text-red-400 flex items-center gap-1.5 hover:text-red-300 transition-colors border border-red-500/20 hover:bg-red-500/10 px-3 py-1.5 rounded-lg"
                            >
                                <FiTrash2 size={12} /> Hapus Foto
                            </button>
                        )}

                        {!profilePreview && !isCompressing && (
                            <p className="text-[10px] text-slate-600 flex items-center gap-1">
                                <span className="px-1.5 py-0.5 bg-slate-800 rounded border border-slate-700 font-mono">Ctrl+V</span>
                                untuk paste gambar
                            </p>
                        )}
                    </div>
                </div>

                {/* FORM INPUT DATA USER */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="md:col-span-2">
                        <label htmlFor="name" className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Nama Lengkap</label>
                        <input
                            id="name"
                            type="text"
                            value={userData.name}
                            onChange={handleUserChange}
                            placeholder="Masukkan nama lengkap Anda"
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Alamat Email</label>
                        <input
                            id="email"
                            type="email"
                            value={userData.email}
                            onChange={handleUserChange}
                            placeholder="email@contoh.com"
                            disabled
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label htmlFor="phone_number" className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">No Telepon</label>
                        <input
                            id="phone_number"
                            type="tel"
                            value={userData.phone_number}
                            onChange={handleUserChange}
                            placeholder="08xxxxxxxxxx"
                            className={inputClass}
                        />
                    </div>
                </div>
            </section>

            <div
                className="px-6 pt-4 flex justify-end"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.3)" }}
            >
                <button
                    onClick={handleSubmit}
                    disabled={loadingSubmit}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                        background: "linear-gradient(135deg, #0077B6, #00B4D8)",
                        boxShadow: "0 4px 15px rgba(0, 180, 216, 0.2)",
                    }}
                >
                    <FaFloppyDisk className={`${loadingSubmit ? "animate-spin" : ""}`} />
                    {loadingSubmit ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
            </div>
        </>
    )
}